import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, X } from "lucide-react";
import { FullPageLoader } from "../../components/RouteGuards";
import { StatusBadge } from "../../components/register/UnderReview";
import { WizardCtx } from "../../components/register/WizardContext";
import StepReview from "../../components/register/steps/StepReview";
import { EditProfileForm } from "../../components/admin/detail/EditProfileForm";
import { PhotoManager } from "../../components/portal/PhotoManager";
import { useAuth } from "../../context/AuthContext";
import { fromRecords } from "../../lib/formData";
import { supabase } from "../../lib/supabase";

const BANNER = {
  pending: ["bg-[#F5E6BA]/70 text-[#4A000E]", "Your profile is under review."],
  approved: ["bg-emerald-50 text-emerald-900", "Your profile is approved. Our team will share suitable matches with you."],
  rejected: ["bg-red-50 text-red-900", "Your profile was not approved."],
  hold: ["bg-amber-50 text-amber-900", "Your profile is on hold. Our team will contact you."],
  blocked: ["bg-red-100 text-red-900", "Your profile is blocked. Please contact the office."],
  inactive: ["bg-stone-100 text-stone-800", "Your profile is inactive. Please contact the office."],
};
const one = (t, id) => supabase.from(t).select("*").eq("profile_id", id).maybeSingle();

export default function MyProfile() {
  const { user } = useAuth();
  const [rec, setRec] = useState(undefined);
  const [editing, setEditing] = useState(false);
  const [justEdited, setJustEdited] = useState(false);

  const load = useCallback(async () => {
    const { data: profile } = await supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle();
    if (!profile) return setRec(null);
    const [family, priv, prefs, photos] = await Promise.all([
      one("family_details", profile.id), one("profile_private_details", profile.id), one("partner_preferences", profile.id),
      supabase.from("profile_photos").select("*").eq("profile_id", profile.id).order("is_primary", { ascending: false }),
    ]);
    setRec({ profile, family: family.data, priv: priv.data, prefs: prefs.data, photos: photos.data || [] });
  }, [user.id]);
  useEffect(() => { load(); }, [load]);
  const ctx = useMemo(() => rec && ({ data: fromRecords(rec), set: () => {}, errors: {}, userId: user.id, mode: "admin", goTo: () => setEditing(true) }), [rec, user.id]);

  if (rec === undefined) return <FullPageLoader />;
  if (rec === null) {
    return (
      <section data-testid="complete-registration-card" className="rmm-card p-8 text-center">
        <h1 className="font-display text-3xl font-bold text-[#4A000E]">Complete your registration</h1>
        <p className="mt-2 text-sm text-[#8C6A6C]">Your registration form is not finished yet. Continue from where you left off.</p>
        <Link to="/register" data-testid="complete-registration-button" className="mt-5 inline-flex h-11 items-center rounded-full bg-[#7A0016] px-6 text-sm font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">Continue registration</Link>
      </section>
    );
  }
  const p = rec.profile;
  const [cls, msg] = BANNER[p.status] || BANNER.pending;
  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <div data-testid="status-banner" className={`rounded-xl px-4 py-3 text-sm font-medium ${cls}`}>
        <span className="mr-2"><StatusBadge status={p.status} /></span>
        {justEdited && p.status === "pending" ? "Your changes are under review." : msg}
        {p.status === "rejected" && p.rejection_reason && <span data-testid="rejection-reason" className="block pt-1">Reason: {p.rejection_reason}</span>}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">My profile · Reg. no. {p.registration_no}</p>
          <h1 className="font-display text-3xl font-bold text-[#4A000E] sm:text-4xl">{p.full_name}</h1>
        </div>
        <button type="button" onClick={() => setEditing((e) => !e)} data-testid="toggle-edit-profile" className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37] bg-white px-4 py-2 text-sm font-semibold text-[#7A0016] hover:bg-[#F5E6BA]/50">
          {editing ? <><X className="h-4 w-4" />Cancel</> : <><Pencil className="h-4 w-4" />Edit my details</>}
        </button>
      </div>
      <PhotoManager photos={rec.photos} onChanged={load} />
      {editing ? (
        <EditProfileForm key={p.updated_at} rec={rec} onSaved={() => { setEditing(false); setJustEdited(true); load(); }} />
      ) : (
        <WizardCtx.Provider value={ctx}><StepReview hideUploads emptyText="Not provided" /></WizardCtx.Provider>
      )}
    </div>
  );
}
